import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { Category, Size } from "../../../../gobalType";
import {
  isPlantRowSelected,
  isSizeRowSelected,
} from "../utils/filterSelection";

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: 13,
  color: "#4e4747",
  letterSpacing: "0.01em",
  mb: 1,
  mt: 0,
} as const;

const rowSx = (selected: boolean) => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 0,
  py: 0.625,
  px: 1.25,
  mb: 0.25,
  borderRadius: "6px",
  width: "100%",
  boxSizing: "border-box" as const,
  backgroundColor: selected ? "#E7F3E7" : "transparent",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: selected ? "#E7F3E7" : "rgba(0, 0, 0, 0.04)",
  },
});

type PlantsDesktopFilterProps = {
  categories: Category[];
  allSize: Size[];
  arrchoicePlant: number[];
  arrchoiceSize: string[];
  productCount: number;
  onChoicePlant: (cate: Category) => void;
  onChoiceSize: (size: Size) => void;
};

export function PlantsDesktopFilter({
  categories,
  allSize,
  arrchoicePlant,
  arrchoiceSize,
  productCount,
  onChoicePlant,
  onChoiceSize,
}: PlantsDesktopFilterProps) {
  const hasActiveFilters =
    arrchoicePlant.length > 0 || arrchoiceSize.length > 0;
  const resultLabel = productCount === 1 ? "result" : "results";

  return (
    <Box
      sx={{
        display: { xs: "none", sm: "block" },
        borderRadius: "8px",
        marginTop: { xs: "65px", sm: 0 },
        backgroundColor: "#fff",
        width: { sm: "228px" },
        minWidth: { sm: "228px" },
        py: 2,
        px: 2,
        boxShadow: "0px 3px 12px rgba(0, 0, 0, 0.1)",
        ml: { sm: 0, md: -6, lg: -8 },
        mr: 2,
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 15,
          lineHeight: 1.3,
          color: "#111",
          mb: 2,
          mt: 0,
        }}
      >
        Filter by
      </Typography>

      <Typography sx={sectionTitleSx}>Categories</Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mb: 0 }}>
        {categories?.map((cate, index) => {
          const selected = isPlantRowSelected(
            cate.id,
            arrchoicePlant,
            categories
          );
          return (
            <Box
              component="li"
              onClick={() => onChoicePlant(cate)}
              key={index}
              sx={rowSx(selected)}
            >
              <Typography
                sx={{
                  mb: 0,
                  fontSize: 14,
                  lineHeight: 1.35,
                  color: selected ? "#263238" : "#979797",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {cate?.name}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        role="separator"
        sx={{
          border: "none",
          borderTop: "1px solid #e0e0e0",
          my: 2,
          mx: 0,
        }}
      />

      <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Plants Size</Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {allSize?.map((size, index) => {
          const selected = isSizeRowSelected(
            size.value,
            arrchoiceSize,
            allSize
          );
          return (
            <Box
              component="li"
              onClick={() => onChoiceSize(size)}
              key={index}
              sx={rowSx(selected)}
            >
              <Typography
                sx={{
                  mb: 0,
                  fontSize: 14,
                  lineHeight: 1.35,
                  color: selected ? "#263238" : "#979797",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {size?.name}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #e8e8e8",
          mt: 2,
          pt: 1.75,
        }}
      >
        <Typography
          sx={{
            mb: 0,
            fontSize: 14,
            fontWeight: 600,
            color: "#333",
          }}
        >
          {hasActiveFilters
            ? `${productCount} ${resultLabel}`
            : "0 results"}
        </Typography>
      </Box>
    </Box>
  );
}
